import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { Toast } from '@elka/ui';
import { DEAL_STAGE_LABELS } from '@elka/types';
import type { Deal, DealStage } from '@elka/types';
import { useDeals } from '../deals/hooks/useDeals';
import { stageChangePatch } from '../deals/utils/stagePatch';
import { waitPatch } from '../deals/utils/waiting';
import { QuickLogModal, type QuickLogRequest } from './QuickLogModal';
import {
  CrmActionsContext,
  type CrmActions,
  type CrmToastType,
  type OpenLogOptions,
  type OpenNewContactOptions,
} from './useCrmActions';

/**
 * Owns the CRM's app-wide UI: the toast, the quick-log / new-contact dialog,
 * plus the deal writes every surface shares (stage, waiting). Mounted once in Layout; read with useCrmActions().
 */
export function CrmActionsProvider({ children }: { children: ReactNode }) {
  const { deals, updateDeal } = useDeals();
  const [toastState, setToastState] = useState<{
    id: number;
    message: string;
    type: CrmToastType;
  } | null>(null);
  const [dialog, setDialog] = useState<QuickLogRequest | null>(null);

  // The actions stay stable across renders (so consumers don't re-render with every
  // deals snapshot) and read the latest list and writer from here.
  const latest = useRef({ deals, updateDeal });
  useEffect(() => {
    latest.current = { deals, updateDeal };
  });

  const toast = useCallback((message: string, type: CrmToastType = 'success') => {
    setToastState((current) => ({ id: (current?.id ?? 0) + 1, message, type }));
  }, []);

  // Per toast, so a replaced toast's late close can't dismiss its successor; stable while
  // it shows, or Toast's timer would restart on every render.
  const toastId = toastState?.id;
  const closeToast = useCallback(
    () => setToastState((current) => (current?.id === toastId ? null : current)),
    [toastId]
  );

  const changeStage = useCallback(
    async (deal: Deal, next: DealStage) => {
      if (deal.stage === next) return;
      try {
        await latest.current.updateDeal(deal.id, stageChangePatch(deal, next));
        toast(`״${deal.name}״ → ${DEAL_STAGE_LABELS[next]}`);
      } catch (err) {
        console.error('Error changing deal stage:', err);
        toast('שגיאה בעדכון שלב העסקה. נסה שוב.', 'error');
      }
    },
    [toast]
  );

  const setWaiting = useCallback(
    async (deal: Deal, days: number | null) => {
      const patch = waitPatch(days, deal);
      try {
        await latest.current.updateDeal(deal.id, patch);
        toast(
          days === null
            ? 'ההמתנה בוטלה'
            : `ממתין ללקוח עד ${format(new Date(`${patch.waitingUntil}T00:00:00`), 'dd/MM')}`
        );
      } catch (err) {
        console.error('Error updating deal wait:', err);
        toast('שגיאה בעדכון ההמתנה. נסה שוב.', 'error');
      }
    },
    [toast]
  );

  const openLog = useCallback(
    (type: 'call' | 'meeting', opts?: OpenLogOptions) => setDialog({ kind: type, ...opts }),
    []
  );
  const openNewContact = useCallback(
    (opts?: OpenNewContactOptions) => setDialog({ kind: 'contact', ...opts }),
    []
  );
  const closeDialog = useCallback(() => setDialog(null), []);

  const actions = useMemo<CrmActions>(
    () => ({ toast, openLog, openNewContact, changeStage, setWaiting }),
    [toast, openLog, openNewContact, changeStage, setWaiting]
  );

  return (
    <CrmActionsContext.Provider value={actions}>
      {children}
      {dialog && <QuickLogModal request={dialog} onClose={closeDialog} />}
      {toastState && (
        <Toast
          key={toastState.id}
          message={toastState.message}
          type={toastState.type}
          duration={toastState.type === 'error' ? 5000 : 3000}
          onClose={closeToast}
        />
      )}
    </CrmActionsContext.Provider>
  );
}
